import { create } from 'zustand'
import { toast } from 'sonner'
import { api, ApiRequestError } from '@/lib/api'
import { documentJobLine, type DocumentJobView } from '@/lib/document-job-progress'
import { useScheduleStore } from '@/stores/schedule-store'
import { useWordFormStore } from '@/stores/word-form-store'

export type ActiveJobStatus = 'pending' | 'running' | 'completed' | 'failed' | 'cancelled'
export type ActiveJobType =
  | 'draft_generation'
  | 'multi_segment_regeneration'
  | 'full_plan_generation'
  | 'schedule_fill'
  /** A background fill run for a buyer Word form (R3 T5). */
  | 'form_fill'
  /** A whole-document job (R1). It may have no session or turn (button and answer starts). */
  | 'document_job'

export interface ActiveJob {
  jobId: string
  sessionId: string | null
  turnId: string | null
  organizationId: string
  userId: string
  artifactId: string | null
  bidId: string | null
  sectionKey: string | null
  title: string | null
  jobType: ActiveJobType
  status: ActiveJobStatus
  progress: number
  progressMessage: string | null
  errorMessage: string | null
  createdAt: string
  updatedAt: string
  /** Document jobs only: saved progress facts, queue place, outcome (R1 P3′). */
  document?: DocumentJobView
}

/** Cancel on a document job: done, or refused with the server's plain line. */
export type DocumentJobCancelResult = { ok: true } | { ok: false; message: string }

const CANCEL_FAILED_MESSAGE = "Couldn't cancel it. Try again in a moment."

interface ActiveJobsListResponse {
  items: ActiveJob[]
}

// Polling is adaptive: 5 s while there is work to watch, dropping to a slow
// heartbeat once the active list has come back empty a few times in a row, and
// pausing entirely on a hidden tab.  A fixed 5 s interval on every authenticated
// route cost ~720 VPC Lambda + RDS round trips per hour per open tab for a
// result that is almost always empty.
const ACTIVE_POLL_INTERVAL_MS = 5_000
const IDLE_POLL_INTERVAL_MS = 60_000
const EMPTY_POLLS_BEFORE_BACKOFF = 3

function isTerminal(status: ActiveJobStatus): boolean {
  return status === 'completed' || status === 'failed' || status === 'cancelled'
}

/** Background fill runs stage answers straight into the database — no stream. */
function isFillJob(job: ActiveJob): boolean {
  return job.jobType === 'schedule_fill' || job.jobType === 'form_fill'
}

/**
 * Show what a fill run staged on the open workbook or form. A run that
 * failed or was cancelled part-way still staged the rows it got through.
 */
function refreshOpenFillProposals(job: ActiveJob): void {
  if (!job.artifactId) return
  const wordForm = useWordFormStore.getState()
  if (job.jobType === 'form_fill' && wordForm.artifactId === job.artifactId) {
    void wordForm.refreshProposals()
  }
  const schedule = useScheduleStore.getState()
  if (job.jobType === 'schedule_fill' && schedule.artifactId === job.artifactId) {
    void schedule.refreshProposals()
  }
}

function isTabHidden(): boolean {
  return typeof document !== 'undefined' && document.hidden
}

/**
 * "Empty" means nothing left to watch — carried terminal rows still render in
 * the tray but need no fast refresh, so they do not hold the fast cadence open.
 */
function nextEmptyCount(previous: number, jobs: ActiveJob[]): number {
  return jobs.some((job) => !isTerminal(job.status)) ? 0 : previous + 1
}

interface ActiveJobsState {
  jobs: ActiveJob[]
  dismissedIds: Set<string>
  /**
   * R1 UX1: the newest document job on a document, by artifact id, read for
   * its overview (`GET /agent/jobs?status=latest`). Kept apart from `jobs`
   * so a long-finished job never comes back into the tray or toasts.
   */
  latestDocumentJobs: Record<string, ActiveJob>
  isLoading: boolean
  pollHandle: ReturnType<typeof setTimeout> | null
  /**
   * Whether polling is logically on.  Distinct from `pollHandle`, which is null
   * while paused on a hidden tab — this is the StrictMode-safe start guard.
   */
  isPolling: boolean
  consecutiveEmptyPolls: number

  fetchActive: () => Promise<void>
  startPolling: () => void
  stopPolling: () => void
  scheduleNextPoll: () => void
  /**
   * Sign-out hook for resetAllStores. The generic `setState(initialState, true)`
   * nulls `pollHandle` without clearing the timer, so one orphan fetchActive
   * would still fire after sign-out; stopPolling cancels it and drops the
   * visibilitychange listener that setState cannot reach.
   */
  reset: () => void
  /** Called when agent work is dispatched, to restore the fast cadence at once. */
  notifyJobDispatched: () => void
  upsertJob: (job: ActiveJob) => void
  dismissJob: (jobId: string) => void
  /**
   * The panel's own background jobs for a session. Document jobs are never
   * among them: they have their own progress card (PanelDocumentJob), and
   * treating one as the panel's job would poll and Stop it by session.
   */
  getJobsForSession: (sessionId: string) => ActiveJob[]
  /** The newest document job on a document still in the list (not dismissed), or null. */
  getDocumentJobForArtifact: (artifactId: string) => ActiveJob | null
  /** Cancel a document job by id (POST /agent/cancel { jobId }), then refresh. */
  cancelDocumentJob: (jobId: string) => Promise<DocumentJobCancelResult>
  /** R1 UX1: read the newest document job on a document (any age). Never throws. */
  loadLatestDocumentJob: (artifactId: string) => Promise<void>
  /**
   * R1 UX1: the document job the overview shows — the tray's live one, or the
   * document's latest; for the same job, the fresher copy (the one carrying
   * its recap on a tie). Dismissed jobs are not shown.
   */
  getOverviewDocumentJob: (artifactId: string) => ActiveJob | null
}

export const useActiveJobsStore = create<ActiveJobsState>()((set, get) => ({
  jobs: [],
  dismissedIds: new Set(),
  latestDocumentJobs: {},
  isLoading: false,
  pollHandle: null,
  isPolling: false,
  consecutiveEmptyPolls: 0,

  fetchActive: async () => {
    const currentlyLoading = get().isLoading
    if (!currentlyLoading) set({ isLoading: true })
    try {
      const data = await api.get<ActiveJobsListResponse>('/agent/jobs', {
        status: 'active',
      })
      const prevById = new Map(get().jobs.map((j) => [j.jobId, j]))
      const nextIds = new Set(data.items.map((j) => j.jobId))

      // A fill run that moved since the last poll staged new answers.
      for (const job of data.items) {
        const prev = prevById.get(job.jobId)
        if (isFillJob(job) && prev && job.progress > prev.progress) refreshOpenFillProposals(job)
      }

      // Detect transitions: jobs that were running but disappeared from the
      // active list have finished since the last poll.  Fetch recent jobs so
      // the tray can surface them briefly and we emit completion toasts.
      const disappeared = [...prevById.values()].filter(
        (j) => !nextIds.has(j.jobId) && !isTerminal(j.status)
      )

      // Preserve any terminal jobs the user hasn't dismissed yet — the server's
      // active list only returns pending/running, so on every subsequent poll
      // after a completion we'd otherwise drop the just-finished row.
      const dismissed = get().dismissedIds
      const carryTerminal = [...prevById.values()].filter(
        (j) => isTerminal(j.status) && !nextIds.has(j.jobId) && !dismissed.has(j.jobId)
      )

      if (disappeared.length > 0) {
        try {
          const recent = await api.get<ActiveJobsListResponse>('/agent/jobs', {
            status: 'recent',
          })
          for (const job of recent.items) {
            const prev = prevById.get(job.jobId)
            if (prev && !isTerminal(prev.status) && isTerminal(job.status)) {
              emitTerminalToast(job)
              if (isFillJob(job)) refreshOpenFillProposals(job)
            }
          }
          // Merge active jobs + fresh terminal rows + any prior terminal rows
          // we were already showing that are still within the recent window.
          const recentTerminalIds = new Set(
            recent.items.filter((r) => isTerminal(r.status)).map((r) => r.jobId)
          )
          const merged = [
            ...data.items,
            ...recent.items.filter((r) => !nextIds.has(r.jobId) && isTerminal(r.status) && !dismissed.has(r.jobId)),
            ...carryTerminal.filter((j) => !recentTerminalIds.has(j.jobId)),
          ]
          set({
            jobs: merged,
            isLoading: false,
            consecutiveEmptyPolls: nextEmptyCount(get().consecutiveEmptyPolls, merged),
          })
          return
        } catch {
          // Recent fetch failed — fall through to the simple merge below
        }
      }

      const merged = [...data.items, ...carryTerminal]
      set({
        jobs: merged,
        isLoading: false,
        consecutiveEmptyPolls: nextEmptyCount(get().consecutiveEmptyPolls, merged),
      })
    } catch {
      set({ isLoading: false })
    }
  },

  startPolling: () => {
    // StrictMode double-invokes effects, so guard on the logical flag rather
    // than the timer handle (which is null while paused on a hidden tab).
    if (get().isPolling) return
    set({ isPolling: true, consecutiveEmptyPolls: 0 })
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', handleVisibilityChange)
    }
    void get().fetchActive()
    get().scheduleNextPoll()
  },

  stopPolling: () => {
    const handle = get().pollHandle
    if (handle) clearTimeout(handle)
    if (typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
    set({ pollHandle: null, isPolling: false, consecutiveEmptyPolls: 0 })
  },

  scheduleNextPoll: () => {
    const existing = get().pollHandle
    if (existing) clearTimeout(existing)

    if (!get().isPolling) {
      set({ pollHandle: null })
      return
    }

    // Paused — `handleVisibilityChange` reschedules when the tab comes back.
    if (isTabHidden()) {
      set({ pollHandle: null })
      return
    }

    const delay =
      get().consecutiveEmptyPolls >= EMPTY_POLLS_BEFORE_BACKOFF
        ? IDLE_POLL_INTERVAL_MS
        : ACTIVE_POLL_INTERVAL_MS

    const handle = setTimeout(() => {
      void get()
        .fetchActive()
        .finally(() => get().scheduleNextPoll())
    }, delay)
    set({ pollHandle: handle })
  },

  reset: () => {
    get().stopPolling()
  },

  notifyJobDispatched: () => {
    set({ consecutiveEmptyPolls: 0 })
    if (!get().isPolling || isTabHidden()) return
    void get().fetchActive()
    get().scheduleNextPoll()
  },

  upsertJob: (job: ActiveJob) => {
    const { jobs } = get()
    const existing = jobs.find((j) => j.jobId === job.jobId)
    if (existing && !isTerminal(existing.status) && isTerminal(job.status)) {
      emitTerminalToast(job)
    }
    if (existing) {
      set({ jobs: jobs.map((j) => (j.jobId === job.jobId ? job : j)) })
    } else {
      set({ jobs: [job, ...jobs] })
    }
    // A newly-arrived live job means there is something to watch again.
    if (!isTerminal(job.status)) {
      get().notifyJobDispatched()
    }
  },

  dismissJob: (jobId: string) => {
    const dismissed = new Set(get().dismissedIds)
    dismissed.add(jobId)
    const latest = Object.fromEntries(
      Object.entries(get().latestDocumentJobs).filter(([, job]) => job.jobId !== jobId)
    )
    set({
      dismissedIds: dismissed,
      jobs: get().jobs.filter((j) => j.jobId !== jobId),
      latestDocumentJobs: latest,
    })
  },

  getJobsForSession: (sessionId: string) =>
    get().jobs.filter((j) => j.sessionId === sessionId && j.jobType !== 'document_job'),

  getDocumentJobForArtifact: (artifactId: string) => {
    const candidates = get().jobs.filter((j) => j.jobType === 'document_job' && j.artifactId === artifactId)
    if (candidates.length === 0) return null
    return candidates.reduce((newest, job) => (job.createdAt > newest.createdAt ? job : newest))
  },

  loadLatestDocumentJob: async (artifactId: string) => {
    try {
      const data = await api.get<ActiveJobsListResponse>('/agent/jobs', { status: 'latest', artifactId })
      const latest = data.items.find((job) => job.jobType === 'document_job' && job.artifactId === artifactId)
      const next = { ...get().latestDocumentJobs }
      if (latest) next[artifactId] = latest
      else delete next[artifactId]
      set({ latestDocumentJobs: next })
    } catch {
      // Best effort: the overview keeps what it had.
    }
  },

  getOverviewDocumentJob: (artifactId: string) => {
    const live = get().getDocumentJobForArtifact(artifactId)
    const latest = get().latestDocumentJobs[artifactId]
    if (!latest || get().dismissedIds.has(latest.jobId)) return live
    if (!live) return latest
    if (live.jobId !== latest.jobId) return live.createdAt >= latest.createdAt ? live : latest
    if (live.updatedAt !== latest.updatedAt) return live.updatedAt > latest.updatedAt ? live : latest
    return latest.document?.recap && !live.document?.recap ? latest : live
  },

  cancelDocumentJob: async (jobId: string) => {
    try {
      await api.post('/agent/cancel', { jobId })
      void get().fetchActive()
      return { ok: true }
    } catch (error) {
      return { ok: false, message: error instanceof ApiRequestError ? error.message : CANCEL_FAILED_MESSAGE }
    }
  },
}))

/**
 * Module-level so add/removeEventListener see the same reference and StrictMode
 * double-mounts cannot stack duplicate listeners.
 */
function handleVisibilityChange(): void {
  const store = useActiveJobsStore.getState()
  if (!store.isPolling) return

  if (isTabHidden()) {
    // scheduleNextPoll clears the pending timer and declines to set a new one.
    store.scheduleNextPoll()
    return
  }

  // Back in view — refresh at once so the tray is never stale, then resume.
  void store.fetchActive()
  store.scheduleNextPoll()
}

function emitTerminalToast(job: ActiveJob): void {
  const label = job.title ?? 'AI task'
  if (job.jobType === 'document_job' && job.status === 'completed') {
    toast.success(`${label} finished`, { description: documentJobLine(job) })
    return
  }
  if (job.status === 'completed') {
    toast.success(`${label} finished`)
  } else if (job.status === 'failed') {
    toast.error(`${label} failed`, {
      description: job.errorMessage ?? undefined,
    })
  }
}
